// @flow strict
import * as React from 'react';
import useLabelTranslation from 'src/hooks/useLabelTranslation';
import {useSelector} from 'react-redux';

import {
  Card,
  CardHeader,
  CardContent,
} from '@spaced-out/ui-design-system/lib/components/Card';
import {
  Table,
  TableTopBar,
} from '@spaced-out/ui-design-system/lib/components/Table';
import {SubTitleSmall} from '@spaced-out/ui-design-system/lib/components/Text';
import {Icon} from '@spaced-out/ui-design-system/lib/components/Icon';
import {Tooltip} from '@spaced-out/ui-design-system/lib/components/Tooltip';

import {
  selectApplicantInformation,
  selectJobScoreCards,
} from 'src/selectors/crm';

import css from './scorecard.css';


const flatten = (arr) =>
  arr.reduce(
    (flat, toFlatten) =>
      // $FlowFixMe[incompatible-call]
      flat.concat(Array.isArray(toFlatten) ? flatten(toFlatten) : toFlatten),
    [],
  );

const calculateAverage = (score) => {
  if (!score || score.count <= 0) {
    return '-';
  }
  return Math.round((score.total * 10) / score.count) / 10;
};

const ApplicantScorecards = ({jobId}: {jobId: number}): React.Node => {
  const t = useLabelTranslation();
  const applicantDetails = useSelector(selectApplicantInformation);
  const jobScoreCardData = useSelector(selectJobScoreCards);

  const {sections = [], ratingsOutOf} = jobScoreCardData ?? {};

  // Get current job application
  const currentJobApplication = applicantDetails?.applications?.find(
    (application) =>
      application.jobId === jobId &&
      !application.duplicate &&
      application.jobId > 0 &&
      application.active,
  );

  // Get all reviews grouped by step
  const {stepCandidates = []} = currentJobApplication ?? {};
  const allReviewsList = stepCandidates.map(
    (stepCandidate) => stepCandidate.interviewReviews,
  );
  const steps = flatten(allReviewsList).reduce((acc, review) => {
    if (!acc[review.stepId]) {
      acc[review.stepId] = [];
    }
    acc[review.stepId].push(review);
    return acc;
  }, {});

  // Calculate scores
  const feedbackScores = flatten(Object.values(steps)).flatMap(
    //$FlowFixMe[incompatible-use]
    (review) => review.feedbackScores,
  );

  const scoreCountMapByParameter = {};
  const finalScore = {total: 0, count: 0};

  feedbackScores.forEach(({parameterId, score}) => {
    if (score < 0) {
      return;
    }

    // Update final score
    finalScore.total += score;
    if (score > 0) {
      finalScore.count += 1;
    }

    // Update parameter score
    if (!scoreCountMapByParameter[parameterId]) {
      scoreCountMapByParameter[parameterId] = {total: 0, count: 0};
    }
    scoreCountMapByParameter[parameterId].total += score;
    if (score > 0) {
      scoreCountMapByParameter[parameterId].count += 1;
    }
  });

  const overallScore = calculateAverage(finalScore);

  const renderScorecardSection = (section) => {
    const sectionRows = section.parameters.map((parameter) => {
      const averageScore = calculateAverage(
        scoreCountMapByParameter[parameter.id] || {total: 0, count: 0},
      );

      // Get individual reviewer scores
      const scores = Object.keys(steps).reduce((acc, stepId) => {
        steps[stepId].forEach((review, index) => {
          const feedbackScore = review.feedbackScores.find(
            (score) => score.parameterId === parameter.id,
          );
          acc[`reviewer_${stepId}_${index}`] = feedbackScore?.score || '-';
        });
        return acc;
      }, {});

      return {
        id: `parameter_${parameter.id}`,
        parameter: parameter.name,
        averageScore,
        ...scores,
      };
    });

    const headers = [
      {key: 'parameter', label: t('Parameters', 'Parameters')},
      {key: 'averageScore', label: t('Average_Score', 'Average Score')},
      ...Object.keys(steps).flatMap((stepId) =>
        steps[stepId].map((review, index) => ({
          key: `reviewer_${stepId}_${index}`,
          label: review.reviewer.fullName,
        })),
      ),
    ];

    return (
      <div key={section.name} className={css.sectionContainer}>
        <TableTopBar>
          <SubTitleSmall>{section.name}</SubTitleSmall>
        </TableTopBar>
        <Table
          borderRadius="0px 0px 12px 12px"
          headers={headers}
          entries={sectionRows}
          emptyText={'No data available'}
        />
      </div>
    );
  };

  return (
    <div className={css.scorecardContainer}>
      <Card classNames={{wrapper: css.scorecardCardWrapper}}>
        <CardHeader className={css.scorecardHeaderContainer}>
          <SubTitleSmall>
            {t('Overall_Score', 'Overall Score')}:{' '}
            {typeof overallScore === 'number' && !Number.isInteger(overallScore)
              ? overallScore.toFixed(2)
              : overallScore}{' '}
            / {ratingsOutOf}
          </SubTitleSmall>
          <Tooltip
            title={`${t(
              'Reviewer_scores_are_based_on_a',
              'Reviewer scores are based on a',
            )} ${ratingsOutOf}-${t(
              'point_scale_adjustable_by_the_Admin_in_the_organization\'s_Settings',
              'point scale, adjustable by the Admin in the organization\'s Settings',
            )}.`}
            elevation="modal"
            placement="right"
            titleMaxLines={5}
          >
            <Icon name="info-circle" />
          </Tooltip>
        </CardHeader>
        <CardContent className={css.sectionsCardContent}>
          {sections.map(renderScorecardSection)}
        </CardContent>
      </Card>
    </div>
  );
};

export default ApplicantScorecards;
